import React, { ReactNode, useEffect } from "react";
import classnames from "classnames";
import { observer } from "mobx-react";

import { logger } from "@formant/core/common/logger";
import { IRtcPeer } from "@formant/core/model/IRtcPeer";
import { RtcConnectionStatus } from "@formant/core/model/RtcConnectionStatus";
import { Uuid } from "@formant/core/model/Uuid";
import { Fader } from "@formant/ui/component/common/Fader";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import { authStore, userStore } from "../../stores";
import { SessionLifecycle } from "../../teleop/ITeleopSession";
import { reload } from "../../utils/reload";
import { Avatar } from "../common/Avatar";
import { Heading2 } from "../common/Heading2";
import { StandardButton } from "../common/StandardButton";
import { Container } from "../Container";
import * as styles from "./TeleopConnectionStatus.scss";

interface IConnectionStatusUIState {
    title: string;
    copy?: string | ReactNode;
    buttonText?: string;
    buttonAction?: () => void;
    showLoading?: boolean;
    showAvatar?: boolean;
}

interface ITeleopConnectionStatusProps {
    sessionStatus: SessionLifecycle;
    connectionStatus: RtcConnectionStatus;
    owner: boolean | undefined;
    ownerUserId?: Uuid;
    devicePeer: false | undefined | IRtcPeer;
    deviceName?: string;
    onJoin: () => void;
}

const disableTeleopDebugLog = true;

const teleopDebug = (message: string, meta?: object) => {
    if (!disableTeleopDebugLog) {
        logger.debug(message, meta);
    }
};

export const TeleopConnectionStatus: React.FC<ITeleopConnectionStatusProps> =
    observer(
        ({
            sessionStatus,
            connectionStatus,
            owner,
            ownerUserId,
            devicePeer,
            deviceName,
            onJoin,
        }) => {
            const ownerUser =
                ownerUserId !== undefined && userStore.get(ownerUserId);
            const connected =
                connectionStatus === "connected" && sessionStatus === "started";
            const connecting = connectionStatus === "connecting";

            const canTakeover =
                !owner &&
                ownerUser !== undefined &&
                ownerUser === false &&
                ownerUserId !== authStore.userId &&
                sessionStatus === "hasSession";

            const sessionOccupied =
                ownerUserId && !owner && ownerUserId !== authStore.userId;
            const sessionAlreadyOwned = ownerUserId === authStore.userId;
            const sessionTakeOver =
                canTakeover &&
                sessionStatus === "hasSession" &&
                connectionStatus === "disconnected";

            const sessionConnectionError =
                connectionStatus === "disconnected" &&
                (sessionStatus === "error" || sessionStatus === "disconnected");

            useEffect(() => {
                if (
                    canTakeover &&
                    sessionStatus === "hasSession" &&
                    connectionStatus === "disconnected"
                ) {
                    // Automatically trigger the onJoin action
                    onJoin();
                }
            }, [canTakeover, sessionStatus, connectionStatus, onJoin]);

            const getUIState = (): IConnectionStatusUIState => {
                if (devicePeer === false) {
                    return {
                        title: "",
                    };
                }
                if (devicePeer === undefined) {
                    return {
                        title: `${deviceName || "Device"} is unavailable`,
                    };
                }

                if (sessionConnectionError) {
                    return {
                        title: "Connection error",
                        copy: "Connection to the device has been lost. Retrying...",
                    };
                }

                if (sessionOccupied) {
                    const userName = ownerUser
                        ? `${ownerUser.firstName} ${ownerUser.lastName}`
                        : "Another user";
                    return {
                        title: "Session occupied",
                        copy: (
                            <>
                                <p>
                                    <span className={styles.name}>
                                        {userName}
                                    </span>{" "}
                                    is currently in a session with this device.
                                </p>
                                <p>
                                    By confirming takeover, you will be
                                    interrupting the existing teleoperation
                                    session.
                                </p>
                            </>
                        ),
                        buttonText: "Confirm Session Takeover",
                        buttonAction: onJoin,
                        showAvatar: true,
                    };
                }

                if (sessionAlreadyOwned) {
                    return {
                        title: "Already owns a session",
                        copy: (
                            <>
                                <p>
                                    It looks like your account is already in a
                                    session with this device
                                </p>
                                <p>
                                    By confirming takeover, your other sessions
                                    will be disconnected
                                </p>
                            </>
                        ),
                        buttonText: "Start Session",
                        buttonAction: onJoin,
                        showAvatar: true,
                    };
                }

                if (sessionTakeOver) {
                    return {
                        title: "Connecting to session",
                        showLoading: true,
                    };
                }

                switch (sessionStatus) {
                    case "hasSession":
                        return {
                            title: "Session available",
                            buttonText: "Start session",
                            buttonAction: onJoin,
                        };
                    case "confirmed":
                        return {
                            title: "Connecting to session",
                            showLoading: true,
                        };
                    case "disconnected":
                        return {
                            title: "Session disconnected",
                            buttonText: "Reload",
                            buttonAction: reload,
                        };
                    default:
                        return { title: "" };
                }
            };

            const uiState = getUIState();

            const debugInfo = {
                uiState: {
                    title: uiState.title,
                    buttonText: uiState.buttonText,
                    showLoading: uiState.showLoading,
                    showAvatar: uiState.showAvatar,
                },
                session: sessionStatus,
                connection: connectionStatus,
                isOwner: owner,
                isConnected: connected,
                isConnecting: connecting,
                takeover: canTakeover,
                isTakingOver: sessionTakeOver,
                isSessionOccupied: sessionOccupied,
                connectionError: sessionConnectionError,
            };
            teleopDebug("TeleopConnectionStatus", debugInfo);
            return (
                <Fader
                    className={classnames(
                        styles["teleop-connection-status"],
                        styles[sessionStatus],
                    )}
                    visible={!connected}
                    slow={true}
                >
                    <Container narrow={true}>
                        <Heading2
                            className={styles["session-status"]}
                            data-testid="teleop-connection-status-message"
                        >
                            {uiState.title}
                        </Heading2>
                        {deviceName && devicePeer && (
                            <div className={styles["device-name"]}>
                                {deviceName}
                            </div>
                        )}
                        <div
                            className={classnames(
                                styles["takeover-message"],
                                styles["can-takeover"],
                            )}
                        >
                            {uiState.showAvatar && (
                                <Avatar
                                    userId={ownerUserId}
                                    size="large"
                                    className={styles.avatar}
                                />
                            )}
                            {uiState.copy}
                        </div>
                        <div className={styles.actions}>
                            <LoadingIndicator
                                visible={uiState.showLoading || connecting}
                            />
                            <Fader
                                visible={!connecting && !!uiState.buttonText}
                                slow={true}
                            >
                                {uiState.buttonText && (
                                    <StandardButton
                                        primary={true}
                                        onClicked={uiState.buttonAction}
                                        data-testid={`teleop-${uiState.buttonText.toLowerCase().replace(" ", "-")}-button`}
                                    >
                                        {uiState.buttonText}
                                    </StandardButton>
                                )}
                            </Fader>
                        </div>
                    </Container>
                </Fader>
            );
        },
    );
